'use client'

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'

import { fetchVenueSchedules, fetchVenueStatus } from '@/service/venue-status.api'
import { useExchangeStatusStream } from '@/service/ws/use-exchange-status-stream'
import { Exchange } from '@/types/generated/common/types'
import { ExchangeStatus, VenueSchedule } from '@/types/generated/public_api'

export const venueStatusKeys = {
  all: ['venueStatus'] as const,
  byExchange: (exchange: Exchange) => [...venueStatusKeys.all, exchange] as const,
  schedules: ['venueSchedules'] as const,
}

/** True for always-open (crypto) venues: "open since forever", no next boundary. */
export function isAlwaysOpen(status: ExchangeStatus): boolean {
  return status.nextChangeMs === undefined && status.sinceMs === '0'
}

/**
 * Live OPEN/CLOSED status for a venue. The REST fetch bootstraps the value and
 * acts as a boundary-driven safety-net poll (refresh shortly after
 * `next_change_ms`, never a fixed timer; always-open venues never refetch).
 * A parallel `ExchangeStatusStream` WS subscription pushes updates into the
 * same cache entry the instant the server sees a change (including
 * MAINTENANCE/UNPLANNED_OUTAGE transitions the REST poll wouldn't otherwise
 * notice until its next scheduled refetch).
 */
export function useVenueStatus(exchange: Exchange | undefined) {
  const queryClient = useQueryClient()
  const enabled = exchange !== undefined && exchange !== Exchange.EXCHANGE_UNSPECIFIED
  const queryKey = venueStatusKeys.byExchange(exchange ?? Exchange.EXCHANGE_UNSPECIFIED)

  const query = useQuery({
    queryKey,
    queryFn: () => fetchVenueStatus(exchange!),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const status = query.state.data
      if (!status) return false
      if (status.nextChangeMs === undefined) return false // always-open: never flips
      // Re-fetch shortly after the boundary (+1s guard), but never tighter than 5s.
      const delay = Number(status.nextChangeMs) - Date.now() + 1_000
      return Math.max(delay, 5_000)
    },
  })

  useExchangeStatusStream({
    connected: enabled,
    payload: { exchange: exchange ?? Exchange.EXCHANGE_UNSPECIFIED },
    onMessage: (status) => {
      if (!status) return
      // Keep the previous object when the push carries no actual change —
      // every decoded WS message is a fresh object, and swapping it in
      // unconditionally re-renders every badge subscribed to this venue
      // (account selector renders one per account row plus the trigger) on
      // every periodic liveness push.
      // `latencyMs` is part of the compared identity: the server deliberately
      // rebroadcasts on a latency-only change so this readout tracks the
      // beacon heartbeat. Omitting it here would discard every one of those
      // pushes (the other three fields sit still in steady state) and freeze
      // the number at whatever the REST bootstrap happened to return.
      queryClient.setQueryData(queryKey, (prev: ExchangeStatus | undefined) =>
        prev &&
        prev.status === status.status &&
        prev.sinceMs === status.sinceMs &&
        prev.nextChangeMs === status.nextChangeMs &&
        prev.latencyMs === status.latencyMs
          ? prev
          : status
      )
    },
  })

  return query
}

/**
 * Weekly "Regular Hours" plus dated holidays / shortened sessions for every
 * venue, cached for the whole session. Hoisted so non-hook callers (the chart
 * datafeed's symbol resolver) share the same cache entry as the hook.
 */
export const venueSchedulesQueryOptions = queryOptions({
  queryKey: venueStatusKeys.schedules,
  queryFn: fetchVenueSchedules,
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: false,
})

/**
 * Display-only: the live badge must come from `useVenueStatus` (overnight
 * sessions and live outages are not reflected here).
 */
export function useVenueSchedules() {
  return useQuery(venueSchedulesQueryOptions)
}

export function useVenueSchedule(exchange: Exchange | undefined): VenueSchedule | undefined {
  const { data: schedules } = useVenueSchedules()
  if (exchange === undefined) return undefined
  return schedules?.find((schedule) => schedule.exchange === exchange)
}
